import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { JSONSchema7 } from "json-schema";
import * as JsonRefs from "json-refs";
import jp from "jsonpath";
import { useFormDataStore } from "../../../store/formDataStore";
import {
  getReportMetadata,
  getIncidentData,
  getEvidenceData,
} from "../../../api/reportApi";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { useErrorSnackbar } from "../../../shared/hooks/useErrorSnackbar";
import { rmsClient } from "../../../api/clients";

export const useReportData = () => {
  const { reportID } = useParams();
  const navigate = useNavigate();
  const { showError, showWarning } = useErrorSnackbar();

  const [videoData, setVideoData] = useState<VideoData | VideoData[] | null>(
    null,
  );
  const [videoLoadStatus, setVideoLoadStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [jsonSchema, setJsonSchema] = useState<JSONSchema7 | null>(null);
  const [reportType, setReportType] = useState<string>("");
  const [lastModifiedTime, setLastModifiedTime] = useState<Date | null>(null);
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [rms, setRms] = useState<string>("");

  const fetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;

        if (metadata.reportStatus !== "PENDING REVIEW") {
          navigate(`/preview-report-info/${reportID}`);
          return;
        }

        setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
        setEnableEditing(metadata.canEdit);
        setRms(metadata.rms || "KAR");

        if (!metadata.canEdit) {
          showWarning(
            "This report is currently locked for editing because another user generated it. You can view and freely navigate through all sections, but editing is disabled. If you believe you should have access, please contact your system administrator.",
          );
        }

        Promise.all(metadata.evidenceIds.map((id) => getEvidenceData(id)))
          .then((evidenceResults) => {
            setVideoData(evidenceResults);
            setVideoLoadStatus("loaded");
          })
          .catch((err) => {
            console.error("Error fetching evidence data:", err);
            setVideoLoadStatus("error");
            showError(
              "An error occurred while fetching evidence data. Please try again later.",
            );
          });
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        const { initStore } = useFormDataStore.getState();

        setLastModifiedTime(
          incidentResult.value.updatedAt
            ? new Date(incidentResult.value.updatedAt)
            : new Date(),
        );
        initStore(incidentResult.value.reportContent, 0);
        setReportType(incidentResult.value.reportType);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      const { destroyStore } = useFormDataStore.getState();
      destroyStore();
      showError(
        error?.response?.data?.error ||
          "An error occurred while fetching extracted incident information. Please try again later.",
      );
      navigate("/");
    }
  }, [reportID]);

  const reFetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;
        if (metadata.reportStatus !== "PENDING REVIEW") {
          navigate(`/preview-report-info/${reportID}`);
          return;
        }
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        const { setFormData } = useFormDataStore.getState();
        setLastModifiedTime(
          incidentResult.value.updatedAt
            ? new Date(incidentResult.value.updatedAt)
            : new Date(),
        );
        setFormData(incidentResult.value.reportContent);
        setReportType(incidentResult.value.reportType);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      showError(
        error?.response?.data?.error ||
          "An error occurred while fetching extracted incident information. Please try again later.",
      );
      navigate("/");
    }
  }, [reportID, navigate, showError]);

  useEffect(() => {
    fetchExtractedIncident();
  }, [fetchExtractedIncident]);

  useEffect(() => {
    if (!reportType) return;
    rmsClient
      .get(`/schema?reportType=${reportType}`)
      .then((response) =>
        JsonRefs.resolveRefs(response.data.schema as JSONSchema7),
      )
      .then((response) => {
        const resolvedSchema = response.resolved;
        jp.query(resolvedSchema, "$..[?(@.oneOf)]").forEach((property: any) => {
          property.enum = property.oneOf.map((option: any) => option.const);
        });
        setJsonSchema(resolvedSchema as JSONSchema7);
        setLoading(false);
      });
  }, [reportType]);

  return {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    setLoading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    setEnableEditing,
    reFetchExtractedIncident,
    reportRms: rms,
  };
};
